// Condición de atención prioritaria del trabajador (Anexo 1 TH 1). DATO SENSIBLE: se guarda aparte de la ficha del
// trabajador y solo el administrador de la empresa lo ve y lo cambia (la base lo exige: ni el manager, ni el viewer,
// ni el superadmin). Solo lo que pide TH 1, sin diagnósticos; la enfermedad catastrófica es solo "sí". El adulto mayor
// se calcula de la fecha de nacimiento si existe. Se guarda con su propio botón (fecha y quién lo registró: servidor).

import React, { useEffect, useState } from 'react';
import {
  CONDICIONES, EDAD_ADULTO_MAYOR, ETIQUETA_CONDICION, ETIQUETA_OTRA_CONDICION, TEXTO_POR_QUE, TEXTO_QUIEN_VE, TEXTO_SOLO_ADMIN, editableDe, esAdultoMayorPorEdad,
  guardarCondicion, leerCondicion, textoOrigen, validarCondicion, type EditableCondicion, type FilaCondicion,
} from '../../lib/informes-puesto/ip-prioritaria';

interface Props { companyId: string; workerId: string | null; birthDate: string; esAdmin: boolean }

const caja: React.CSSProperties = { background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 10, padding: 12,
  display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 };
const nota: React.CSSProperties = { color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: 1.45 };
const fila: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', color: '#e2e8f0', fontSize: '0.86rem', minHeight: 36 };

export default function AtencionPrioritariaSeccion({ companyId, workerId, birthDate, esAdmin }: Props) {
  const [fila0, setFila0] = useState<FilaCondicion | null>(null);
  const [c, setC] = useState<EditableCondicion>(editableDe(null));
  const [disponible, setDisponible] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const porEdad = esAdultoMayorPorEdad(birthDate || null, new Date());

  useEffect(() => {
    if (!esAdmin || !workerId) return;
    let vivo = true;
    leerCondicion(companyId, workerId).then(r => { if (!vivo) return; setFila0(r.fila); setC(editableDe(r.fila)); setDisponible(r.disponible); });
    return () => { vivo = false; };
  }, [companyId, workerId, esAdmin]);

  const titulo = <div style={{ color: '#c4b5fd', fontWeight: 700, fontSize: '0.9rem' }}>Condición de atención prioritaria</div>;
  if (!esAdmin) return <div style={caja}>{titulo}<p style={nota}>{TEXTO_SOLO_ADMIN}</p><p style={nota}>{TEXTO_POR_QUE}</p></div>;
  if (!workerId) return <div style={caja}>{titulo}<p style={nota}>Guarda primero el trabajador; después podrás registrar su condición.</p><p style={nota}>{TEXTO_POR_QUE}</p></div>;
  if (!disponible) return <div style={caja}>{titulo}<p style={nota}>Esta función todavía no está disponible para tu empresa.</p></div>;

  const marcar = (k: keyof EditableCondicion, v: boolean) => {
    setOk(''); setError('');
    if (k === 'declared_none') setC(v ? { ...editableDe(null), declared_none: true } : { ...c, declared_none: false });
    else setC({ ...c, [k]: v, declared_none: false, ...(k === 'disability' && !v ? { disability_pct: '' } : {}) });
  };
  const guardar = async () => {
    const e = validarCondicion(c, porEdad);
    if (e) { setError(e); return; }
    setOcupado(true); setError(''); setOk('');
    try { const f = await guardarCondicion(companyId, workerId, fila0, { ...c, older_adult: c.older_adult || porEdad }); setFila0(f); setC(editableDe(f)); setOk('Condición guardada.'); }
    catch (x) { setError(x instanceof Error ? x.message : String(x)); } finally { setOcupado(false); }
  };

  return (
    <div style={caja} aria-label="Condición de atención prioritaria">
      {titulo}
      <p style={nota}>{TEXTO_POR_QUE}</p>
      <p style={nota}>{TEXTO_QUIEN_VE}</p>
      {porEdad && <p style={{ ...nota, color: '#c4b5fd' }}>Por su fecha de nacimiento tiene {EDAD_ADULTO_MAYOR} años o más: cuenta como adulto mayor.</p>}
      {CONDICIONES.map(k => (
        <label key={k} style={fila}>
          <input type="checkbox" checked={c[k] || (k === 'older_adult' && porEdad)} disabled={ocupado || (k === 'older_adult' && porEdad)}
            onChange={e => marcar(k, e.target.checked)} style={{ width: 18, height: 18 }} />
          {ETIQUETA_CONDICION[k]}
        </label>
      ))}
      {/* Solo si vino de la ficha anterior: se puede quitar (al revisarla), no poner. */}
      {fila0?.other_declared && (
        <label style={fila}>
          <input type="checkbox" checked={c.other_declared} disabled={ocupado} onChange={e => marcar('other_declared', e.target.checked)} style={{ width: 18, height: 18 }} />
          {ETIQUETA_OTRA_CONDICION}
        </label>
      )}
      {c.disability && (
        <label style={{ ...nota, display: 'flex', flexDirection: 'column', gap: 4 }}>Porcentaje de discapacidad (si se declara)
          <input inputMode="decimal" value={c.disability_pct} disabled={ocupado} onChange={e => setC({ ...c, disability_pct: e.target.value })}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', padding: '8px 10px', maxWidth: 140 }} />
        </label>
      )}
      <label style={fila}>
        <input type="checkbox" checked={c.declared_none} disabled={ocupado || porEdad} onChange={e => marcar('declared_none', e.target.checked)} style={{ width: 18, height: 18 }} />
        Declarado sin condición (se preguntó y no tiene ninguna)
      </label>
      {fila0 && <p style={nota}>Registrado el {new Date(fila0.registered_at).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' })}.</p>}
      {fila0 && textoOrigen(fila0) && <p style={{ ...nota, color: '#c4b5fd' }}>{textoOrigen(fila0)}</p>}
      {error && <p style={{ ...nota, color: '#f87171' }} role="alert">{error}</p>}
      {ok && <p style={{ ...nota, color: '#2FBF71' }} role="status">{ok}</p>}
      <div>
        <button type="button" onClick={guardar} disabled={ocupado}
          style={{ padding: '9px 14px', borderRadius: 8, fontWeight: 600, fontSize: '0.84rem', minHeight: 40, background: '#a78bfa', border: '1px solid #a78bfa', color: '#0f172a', cursor: ocupado ? 'not-allowed' : 'pointer' }}>
          Guardar condición
        </button>
      </div>
    </div>
  );
}
